/**
 * Store overview — the four numbers a seller opens the app for.
 *
 * WHY held funds and unsettled lead: money the platform is holding, and money earned but not yet
 * in a payout, are the amounts nobody else shows them. Reconciliation results come next because
 * they are only actionable once a payout exists.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, SEVERITY_ORDER, type ConnectionSummary } from "../api";
import { ResultBadge, SeverityBadge, StatusBadge } from "../components/Badges";
import { Amount } from "../components/Amount";
import { ConnectStore } from "../components/ConnectStore";
import { BigNumber, MoneyTotals, Tile } from "../components/Tile";
import { ErrorState, Loading } from "../components/States";
import { feeCoverageGaps, feeCoverageLabel, uncheckedFeeLines } from "../fee-schedule";
import { formatCount } from "../money";
import { formatDateTime, shortExternalId } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

export function Overview(): ReactNode {
  const store = useStore();
  const state = useAsync(() => api.connection(store.id), [store.id]);

  if (state.status === "loading") return <Loading label="Loading store overview" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;
  const { summary } = state.data;

  return (
    <>
      <header className="page-head">
        <div>
          <h1 className="page-title">{store.display_name}</h1>
          <p className="page-sub">
            <span className="mono">{store.external_shop_id}</span> · {store.platform} · <StatusBadge status={store.status} />
          </p>
        </div>
      </header>

      <FeeCoverageNotice summary={summary} storeId={store.id} />

      <div className="tiles">
        <Tile label="Held funds" hint="Reserves and dispute holds the platform has not released." tone={summary.held.length ? "warn" : "neutral"}>
          <MoneyTotals totals={summary.held} emptyLabel="Nothing held" />
        </Tile>
        <Tile label="Pending / unsettled" hint="Earned but not yet in a payout.">
          <MoneyTotals totals={summary.pending} emptyLabel="Nothing pending" />
        </Tile>
        <Tile label="Open discrepancies" hint="Findings from the reconciliation engine." tone={severityTone(summary)}>
          {summary.open_discrepancies.length === 0 ? (
            <p className="tile-none">No open findings</p>
          ) : (
            <ul className="totals">
              {SEVERITY_ORDER.map((severity) => {
                const row = summary.open_discrepancies.find((d) => d.severity === severity);
                return row ? (
                  <li key={severity}>
                    <SeverityBadge severity={severity} />
                    <span className="totals-count">{formatCount(row.count)}</span>
                  </li>
                ) : null;
              })}
            </ul>
          )}
        </Tile>
        <Tile label="Open disputes" hint="Chargebacks and inquiries awaiting a response." tone={summary.disputes_open === "0" ? "neutral" : "bad"}>
          <BigNumber value={summary.disputes_open} />
        </Tile>
      </div>

      <div className="split">
        <section className="card">
          <h2 className="card-title">Last payout</h2>
          {summary.last_payout ? (
            <dl className="kv">
              <div><dt>Payout</dt><dd className="mono" title={summary.last_payout.external_id}>{shortExternalId(summary.last_payout.external_id)}</dd></div>
              <div><dt>Issued</dt><dd>{formatDateTime(summary.last_payout.issued_at)}</dd></div>
              <div><dt>Status</dt><dd><StatusBadge status={summary.last_payout.status} /></dd></div>
              <div><dt>Declared net</dt><dd><Amount minor={summary.last_payout.declared_net_minor} currency={summary.last_payout.currency} strong /></dd></div>
              <div><dt>Result</dt><dd><ResultBadge result={summary.last_payout.result} /></dd></div>
            </dl>
          ) : (
            <p className="state-hint">
              No payout has arrived yet. Shopify Payments pays out on a schedule; until the first payout closes, everything
              earned shows up under <Link to={`/stores/${store.id}/unsettled`}>Unsettled</Link>.
            </p>
          )}
          {summary.last_payout ? <Link className="btn" to={`/stores/${store.id}/payouts`}>All payouts</Link> : null}
        </section>

        <section className="card">
          <h2 className="card-title">Last connection-wide run</h2>
          {summary.last_connection_run ? (
            <>
              <dl className="kv">
                <div><dt>Ran</dt><dd>{formatDateTime(summary.last_connection_run.ran_at)}</dd></div>
                <div><dt>Result</dt><dd><ResultBadge result={asResult(summary.last_connection_run.result)} /></dd></div>
              </dl>
              <Stats stats={summary.last_connection_run.stats} />
            </>
          ) : (
            <p className="state-hint">The nightly cross-payout run (orders, reserves, dispute deadlines) has not run for this store yet.</p>
          )}
        </section>

        <section className="card">
          <h2 className="card-title">Fees kept on refunds</h2>
          <p className="state-hint">
            Shopify does not return the payment-processing fee when an order is refunded, so a fully refunded order still
            costs the original fee. This is normal platform behaviour, not a discrepancy.
          </p>
          <MoneyTotals totals={summary.fees_retained} emptyLabel="No fees kept on refunds" />
        </section>
      </div>

      <section className="card"><ConnectStore /></section>
    </>
  );
}

function severityTone(summary: ConnectionSummary): "neutral" | "warn" | "bad" {
  if (summary.open_discrepancies.some((d) => d.severity === "ALERT")) return "bad";
  return summary.open_discrepancies.length > 0 ? "warn" : "neutral";
}

/** The run result column is a free-form string in the DB; only the three engine values colour. */
function asResult(value: string): "RECONCILED" | "DISCREPANT" | "INCOMPLETE" | null {
  return value === "RECONCILED" || value === "DISCREPANT" || value === "INCOMPLETE" ? value : null;
}

/** Run stats are a JSON blob whose keys differ per adapter — render whatever came back. */
function Stats({ stats }: { stats: Record<string, unknown> }): ReactNode {
  const entries = Object.entries(stats).filter(([, v]) => typeof v === "string" || typeof v === "number" || typeof v === "boolean");
  if (entries.length === 0) return null;
  return (
    <dl className="kv kv-dense">
      {entries.map(([key, value]) => (
        <div key={key}><dt>{key}</dt><dd className="mono">{String(value)}</dd></div>
      ))}
    </dl>
  );
}

/**
 * Says out loud when the fee check is switched off.
 *
 * WHY this earns space on the overview: with no fee schedule R8 has nothing to compare a fee
 * against, so the store reports a clean run while a whole rule never ran. A seller reading "no
 * open findings" would reasonably conclude their fees were checked and found correct. This is the
 * one place the product can admit the difference between "checked and fine" and "not checked".
 *
 * It is driven by the store's own lines rather than by a "configured?" flag, because coverage is
 * partial in practice: a SALE/USD schedule leaves a CAD line unchecked, and an Amazon connection
 * settles three currencies at once. Only fee-BEARING lines count — a reserve with no fee has
 * nothing for a schedule to describe.
 */
function FeeCoverageNotice({ summary, storeId }: { summary: ConnectionSummary; storeId: string }): ReactNode {
  const gaps = feeCoverageGaps(summary.fee_coverage);
  if (gaps.length === 0) return null;
  const unchecked = uncheckedFeeLines(summary.fee_coverage);
  return (
    <div className="notice notice-warn">
      <p className="notice-title">Fee checking is off for this store</p>
      <p className="notice-text">
        {formatCount(String(unchecked))} {unchecked === 1n ? "line carries a fee" : "lines carry fees"} that no fee
        schedule prices, so the engine cannot tell whether the platform charged you correctly:{" "}
        {gaps.map(feeCoverageLabel).join(", ")}.{" "}
        <Link to={`/stores/${storeId}/fee-schedule`}>Set the rate this store should be charged</Link> to turn the
        check on. We do not guess a rate — a wrong one would report errors that are ours, not the platform's.
      </p>
    </div>
  );
}
